import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, acquireToken as acquireTokenFromMsal } from './msalConfig';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  roles: string[];
  jobTitle: string;
}

export interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acquireToken: () => Promise<string>;
}

function parseUserFromAccount(account: ReturnType<typeof import('@azure/msal-browser').PublicClientApplication.prototype.getActiveAccount>): AuthUser | null {
  if (!account) return null;

  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const roles = (claims?.roles as string[] | undefined) ?? [];

  const name = account.name ?? '';
  const nameParts = name.trim().split(' ');
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ');

  return {
    id: (claims?.oid as string | undefined) ?? account.localAccountId,
    email: account.username,
    name,
    firstName,
    lastName,
    roles,
    jobTitle: (claims?.jobTitle as string | undefined) ?? '',
  };
}

export function useAuth(): UseAuthReturn {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const isLoading =
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.Logout ||
    inProgress === InteractionStatus.AcquireToken ||
    inProgress === InteractionStatus.Startup;

  const activeAccount = accounts[0] ?? null;
  const user = parseUserFromAccount(activeAccount);

  const login = useCallback(async () => {
    try {
      const response = await instance.loginPopup({
        ...loginRequest,
        loginHint: activeAccount?.username ?? undefined,
      });
      instance.setActiveAccount(response.account);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance, activeAccount]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        account: activeAccount ?? undefined,
        logoutHint: activeAccount?.username ?? undefined,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [instance, activeAccount]);

  const acquireToken = useCallback(async (): Promise<string> => {
    return acquireTokenFromMsal();
  }, []);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    acquireToken,
  };
}
